import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  FileText, Upload, CheckCircle, AlertCircle, Clock, Loader2, 
  Camera, IdCard, Car, Shield, User, CalendarDays, AlertTriangle,
  Download, Eye, RefreshCw, Info, ChevronRight, Lock, Pen
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Document Status Badge Component
function DocStatusBadge({ status }) {
  const statusConfig = {
    not_submitted: { label: 'Not Submitted', color: 'bg-gray-100 text-gray-800', icon: AlertCircle },
    pending: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
    rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: AlertCircle },
    expired: { label: 'Expired', color: 'bg-orange-100 text-orange-800', icon: AlertTriangle }
  };
  
  const config = statusConfig[status] || statusConfig.not_submitted;
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
}

// Document Upload Card Component
function DocumentUploadCard({ doc, onUpload, isUploading }) {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const docTypeConfig = {
    w9_form: { icon: FileText, title: 'W-9 Form', color: 'text-blue-600 bg-blue-100' },
    drivers_license: { icon: IdCard, title: "Driver's License", color: 'text-purple-600 bg-purple-100' },
    vehicle_registration: { icon: Car, title: 'Vehicle Registration', color: 'text-green-600 bg-green-100' },
    insurance_proof: { icon: Shield, title: 'Insurance Proof', color: 'text-orange-600 bg-orange-100' },
    face_photo: { icon: User, title: 'Full Face Photo', color: 'text-pink-600 bg-pink-100' }
  };

  const config = docTypeConfig[doc.type] || docTypeConfig.drivers_license;
  const Icon = config.icon;

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    await onUpload(doc.type, selectedFile);
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const getExpiryInfo = () => {
    if (doc.status === 'approved' && doc.expires_at) {
      const expiryDate = new Date(doc.expires_at);
      const now = new Date();
      const daysLeft = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysLeft <= 30) {
        return { text: `Expires in ${daysLeft} days`, urgent: true };
      }
      return { text: `Valid until ${expiryDate.toLocaleDateString()}`, urgent: false };
    }
    return null;
  };

  const expiryInfo = getExpiryInfo();

  return (
    <Card className={`relative ${doc.status === 'expired' || doc.status === 'rejected' ? 'border-red-300' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${config.color}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <CardTitle className="text-lg">{doc.name}</CardTitle>
              <CardDescription className="text-xs">{doc.description}</CardDescription>
            </div>
          </div>
          <DocStatusBadge status={doc.status || 'not_submitted'} />
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Expiry Warning */}
        {expiryInfo && (
          <div className={`p-2 rounded-lg flex items-center gap-2 text-sm ${expiryInfo.urgent ? 'bg-orange-50 text-orange-700' : 'bg-green-50 text-green-700'}`}>
            <CalendarDays className="w-4 h-4" />
            {expiryInfo.text}
          </div>
        )}

        {/* Current Document Preview */}
        {doc.current_url && (
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-sm text-gray-700 flex-1">Document uploaded</span>
            <Button variant="ghost" size="sm" asChild>
              <a href={doc.current_url} target="_blank" rel="noopener noreferrer">
                <Eye className="w-4 h-4 mr-1" />
                View
              </a>
            </Button>
          </div>
        )}

        {/* Upload Section */}
        {(doc.status !== 'approved' || doc.status === 'expired' || !doc.current_url) && (
          <div className="space-y-3">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept={doc.accepted_formats || 'image/*,.pdf'}
              className="hidden"
            />
            
            {/* File Preview */}
            {previewUrl && (
              <div className="relative">
                <img src={previewUrl} alt="Preview" className="w-full h-40 object-cover rounded-lg" />
                <Button 
                  variant="destructive" 
                  size="sm" 
                  className="absolute top-2 right-2"
                  onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}
                >
                  Remove
                </Button>
              </div>
            )}
            
            {selectedFile && !previewUrl && (
              <div className="p-3 bg-blue-50 rounded-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-blue-700">{selectedFile.name}</span>
              </div>
            )}

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                {selectedFile ? 'Change File' : 'Select File'}
              </Button>
              
              {selectedFile && (
                <Button 
                  onClick={handleUpload}
                  disabled={isUploading}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {isUploading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Upload
                </Button>
              )}
            </div>
            
            <p className="text-xs text-gray-500">
              Accepted: {doc.accepted_formats || 'Images, PDF'} • Max 10MB
            </p>
          </div>
        )}

        {/* Rejection Reason */}
        {doc.status === 'rejected' && doc.rejection_reason && (
          <div className="p-3 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
            <p className="text-sm text-red-700">{doc.rejection_reason}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// W-9 Form Component
function W9FormModal({ isOpen, onClose, onSubmit, initialData }) {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    business_name: initialData?.business_name || '',
    federal_tax_classification: initialData?.federal_tax_classification || 'individual',
    address: initialData?.address || '',
    city: initialData?.city || '',
    state: 'Indiana',
    zip_code: initialData?.zip_code || '',
    ssn_ein: initialData?.ssn_ein || '',
    agree_perjury: false,
    signature_date: new Date().toISOString().split('T')[0]
  });
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const taxClassifications = [
    { value: 'individual', label: 'Individual/Sole Proprietor' },
    { value: 'llc_single', label: 'Single-member LLC' },
    { value: 'llc_c_corp', label: 'LLC taxed as C Corp' },
    { value: 'llc_s_corp', label: 'LLC taxed as S Corp' },
    { value: 'c_corp', label: 'C Corporation' },
    { value: 's_corp', label: 'S Corporation' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'trust_estate', label: 'Trust/Estate' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agree_perjury) {
      toast.error('You must certify the information under penalty of perjury');
      return;
    }
    
    if (!signature.trim()) {
      toast.error('Please provide your digital signature');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({ ...formData, digital_signature: signature });
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit W-9 form');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            IRS W-9 Form (Indiana)
          </DialogTitle>
          <DialogDescription>
            Request for Taxpayer Identification Number and Certification
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Part I - Taxpayer Info */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-3">Part I - Taxpayer Identification</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="name">Name (as shown on your income tax return)*</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  required
                  placeholder="Enter your legal name"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="business_name">Business name/disregarded entity name (if different)</Label>
                <Input
                  id="business_name"
                  value={formData.business_name}
                  onChange={(e) => setFormData(p => ({ ...p, business_name: e.target.value }))}
                  placeholder="Optional"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="tax_class">Federal Tax Classification*</Label>
                <select
                  id="tax_class"
                  value={formData.federal_tax_classification}
                  onChange={(e) => setFormData(p => ({ ...p, federal_tax_classification: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {taxClassifications.map(tc => (
                    <option key={tc.value} value={tc.value}>{tc.label}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="address">Address*</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))}
                  required
                  placeholder="Street address"
                />
              </div>

              <div>
                <Label htmlFor="city">City*</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData(p => ({ ...p, city: e.target.value }))}
                  required
                  placeholder="City"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input id="state" value="Indiana" disabled className="bg-gray-100" />
                </div>
                <div>
                  <Label htmlFor="zip">ZIP Code*</Label>
                  <Input
                    id="zip"
                    value={formData.zip_code}
                    onChange={(e) => setFormData(p => ({ ...p, zip_code: e.target.value }))}
                    required
                    placeholder="ZIP"
                    maxLength={10}
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="ssn_ein">Social Security Number or EIN*</Label>
                <Input
                  id="ssn_ein"
                  value={formData.ssn_ein}
                  onChange={(e) => setFormData(p => ({ ...p, ssn_ein: e.target.value }))}
                  required
                  placeholder="XXX-XX-XXXX or XX-XXXXXXX"
                  type="password"
                />
                <p className="text-xs text-gray-500 mt-1">
                  <Lock className="w-3 h-3 inline mr-1" />
                  Your SSN/EIN is encrypted and stored securely
                </p>
              </div>
            </div>
          </div>

          {/* Part II - Certification */}
          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <h3 className="font-semibold text-yellow-800 mb-3">Part II - Certification</h3>
            
            <div className="text-sm text-gray-700 space-y-2 mb-4">
              <p>Under penalties of perjury, I certify that:</p>
              <ol className="list-decimal list-inside space-y-1 pl-2">
                <li>The number shown on this form is my correct taxpayer identification number</li>
                <li>I am not subject to backup withholding</li>
                <li>I am a U.S. citizen or other U.S. person</li>
                <li>The FATCA code(s) entered on this form (if any) are correct</li>
              </ol>
            </div>

            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border">
              <Checkbox
                id="perjury"
                checked={formData.agree_perjury}
                onCheckedChange={(checked) => setFormData(p => ({ ...p, agree_perjury: checked }))}
              />
              <label htmlFor="perjury" className="text-sm cursor-pointer">
                I certify, under penalty of perjury, that the information provided is true and correct.
              </label>
            </div>
          </div>

          {/* Digital Signature */}
          <div className="p-4 bg-gray-50 rounded-lg border">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Pen className="w-4 h-4" />
              Digital Signature
            </h3>
            
            <div className="space-y-2">
              <Label htmlFor="signature">Type your full legal name as signature*</Label>
              <Input
                id="signature"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Type your full name"
                className="font-cursive text-lg"
                required
              />
              <p className="text-xs text-gray-500">
                Date: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={submitting || !formData.agree_perjury || !signature.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Submit W-9 Form
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Main Provider Documents Component
export default function ProviderDocuments() {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [requirements, setRequirements] = useState(null);
  const [documentStatus, setDocumentStatus] = useState([]);
  const [activeTab, setActiveTab] = useState('documents');
  const [showW9Form, setShowW9Form] = useState(false);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reqRes, statusRes] = await Promise.all([
        axios.get(`${API}/provider-docs/requirements`).catch(() => ({ data: null })),
        axios.get(`${API}/provider-docs/status`, { headers: getAuthHeaders(), withCredentials: true }).catch(() => ({ data: null }))
      ]);
      
      setRequirements(reqRes.data);
      setDocumentStatus(statusRes.data?.documents || []);
    } catch (error) {
      console.error('Failed to fetch document data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (docType, file) => {
    setUploading(prev => ({ ...prev, [docType]: true }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(
        `${API}/provider-docs/upload/${docType}`,
        formData,
        { 
          headers: { 
            ...getAuthHeaders(), 
            'Content-Type': 'multipart/form-data' 
          },
          withCredentials: true 
        }
      );
      
      toast.success('Document uploaded successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(prev => ({ ...prev, [docType]: false }));
    }
  };

  const handleW9Submit = async (formData) => {
    try {
      await axios.post(`${API}/provider-docs/w9/submit`, formData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('W-9 form submitted successfully!');
      setShowW9Form(false);
      fetchData();
    } catch (error) {
      throw error;
    }
  };

  // Merge requirements with status
  const getDocumentWithStatus = (docType) => {
    const req = requirements?.required_documents?.find(d => d.type === docType) || {};
    const status = documentStatus.find(d => d.document_type === docType) || {};
    
    return {
      type: docType,
      name: req.name || docType,
      description: req.description || '',
      accepted_formats: req.accepted_formats,
      status: status.status || 'not_submitted',
      current_url: status.file_url,
      expires_at: status.expires_at,
      rejection_reason: status.rejection_reason
    };
  };

  const documentTypes = ['drivers_license', 'vehicle_registration', 'insurance_proof', 'face_photo'];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
    );
  }

  // Calculate completion status
  const w9Status = documentStatus.find(d => d.document_type === 'w9_form');
  const completedDocs = documentStatus.filter(d => d.status === 'approved').length;
  const totalDocs = documentTypes.length + 1; // +1 for W-9
  const completionPercent = Math.round((completedDocs / totalDocs) * 100);

  return (
    <div className="space-y-6" data-testid="provider-documents">
      {/* Header with Progress */}
      <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Provider Documents</h2>
              <p className="text-blue-100 mt-1">
                Complete all required documents to maintain active provider status
              </p>
            </div>
            <div className="text-right">
              <p className="text-5xl font-bold">{completionPercent}%</p>
              <p className="text-blue-200 text-sm">Complete</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4 h-3 bg-blue-800/50 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white/90 rounded-full transition-all duration-500"
              style={{ width: `${completionPercent}%` }}
            />
          </div>
          
          <div className="flex justify-between mt-2 text-sm text-blue-200">
            <span>{completedDocs} of {totalDocs} documents approved</span>
            <span>Renewal: Every 360 days</span>
          </div>
        </CardContent>
      </Card>

      {/* Info Banner */}
      {completedDocs < totalDocs && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <p className="font-semibold text-yellow-800">Action Required</p>
            <p className="text-sm text-yellow-700">
              You must submit all required documents before you can accept service requests.
              All documents must be renewed every 360 days.
            </p>
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <IdCard className="w-4 h-4" />
            ID & Insurance
          </TabsTrigger>
          <TabsTrigger value="tax-forms" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Tax Forms
          </TabsTrigger>
        </TabsList>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {documentTypes.map(docType => (
              <DocumentUploadCard
                key={docType}
                doc={getDocumentWithStatus(docType)}
                onUpload={handleDocumentUpload}
                isUploading={uploading[docType]}
              />
            ))}
          </div>
        </TabsContent>

        {/* Tax Forms Tab */}
        <TabsContent value="tax-forms">
          <div className="space-y-4">
            {/* W-9 Form Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-100 text-blue-600">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">IRS Form W-9</CardTitle>
                      <CardDescription>Request for Taxpayer Identification Number</CardDescription>
                    </div>
                  </div>
                  <DocStatusBadge status={w9Status?.status || 'not_submitted'} />
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Required for:</strong> All providers earning more than $600 per year.
                    This form is used to report your earnings to the IRS.
                  </p>
                </div>
                
                {w9Status?.status === 'approved' ? (
                  <div className="p-3 bg-green-50 rounded-lg flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">W-9 Form Submitted</p>
                      <p className="text-sm text-green-700">
                        Submitted on {new Date(w9Status.submitted_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ) : (
                  <Button 
                    onClick={() => setShowW9Form(true)} 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="fill-w9-btn"
                  >
                    <Pen className="w-4 h-4 mr-2" />
                    Fill Out W-9 Form
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* 1099 Info Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-green-100 text-green-600">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">1099-NEC Form</CardTitle>
                    <CardDescription>Nonemployee Compensation</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <Info className="w-5 h-5 text-blue-600 mb-2" />
                  <p className="text-sm text-blue-800">
                    <strong>Important:</strong> Your 1099-NEC form will be automatically generated and sent to you by January 31st each year
                    if you earned $600 or more. You do not need to fill this out - it will be provided to you for tax filing purposes.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Indiana State Tax Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-indigo-600" />
                  Indiana State Tax Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="p-3 bg-indigo-50 rounded-lg">
                    <p className="text-sm text-indigo-800">
                      <strong>State Tax Rate:</strong> Indiana has a flat income tax rate of 3.05% (2024)
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>County Tax:</strong> Additional county income tax may apply based on your residence.
                      Fort Wayne/Allen County rate: 1.35%
                    </p>
                  </div>
                  <p className="text-xs text-gray-500">
                    * This is informational only. Consult a tax professional for specific advice.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* W-9 Form Modal */}
      <W9FormModal
        isOpen={showW9Form}
        onClose={() => setShowW9Form(false)}
        onSubmit={handleW9Submit}
        initialData={{ name: user?.name }}
      />
    </div>
  );
}
